import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import MobileBackButton from "@/components/MobileBackButton";
import { useCart } from "@/contexts/CartContext.jsx";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  getDoc,
  // addDoc,
  // serverTimestamp,
  // runTransaction,
  // increment,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage, functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "@/AuthContext";
import { ToastContext } from "@/components/ToastProvider.jsx";
import useVisibilityRefresh from "@/hooks/useVisibilityRefresh";
import MapPicker from "@/components/MapPicker.jsx";
import useUserProfile from "@/hooks/useUserProfile";
import DiscountInput from "@/components/DiscountInput";
import SignUpModal from "@/components/SignUpModal";
import { getStoresByType, STORE_TYPES } from "@/lib/storeManagement";

const MANUAL_METHOD_LABELS = {
  bank_transfer: "Bank Transfer",
  gcash_manual: "GCash (manual)",
  static_qr: "QR (manual)",
};
const COD_LABEL = "Cash on Delivery";

export default function Checkout() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useUserProfile();
  const { storeCarts, getStoreItems, getStoreTotals, clear } = useCart();
  const { notify } = React.useContext(ToastContext);
  const [searchParams] = useSearchParams();

  const [cart, setCart] = useState({ items: [] });
  const [settings, setSettings] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState(null);
  // Distance add-on state (computed via Cloud Function)
  const [distanceAddon, setDistanceAddon] = useState(null);
  const [distanceBusy, setDistanceBusy] = useState(false);
  const [distanceErr, setDistanceErr] = useState("");

  const [qr, setQr] = useState(null);
  const [memberStores, setMemberStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [method, setMethod] = useState("");
  // For non-COD manual methods: pickup vs delivery
  const [deliveryMode, setDeliveryMode] = useState("pickup"); // 'pickup' | 'delivery'
  const [referenceNo, setReferenceNo] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  // COD shipping inputs
  const [shipAddress, setShipAddress] = useState("");
  const [shipPhone, setShipPhone] = useState("");
  const [shipPhoneErr, setShipPhoneErr] = useState("");
  const [shipLat, setShipLat] = useState(null);
  const [shipLng, setShipLng] = useState(null);
  // Discount state
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  // Bump this key to re-subscribe snapshots when refocused
  const [refreshKey, setRefreshKey] = useState(0);
  
  // SignUpModal state
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  useVisibilityRefresh(() => setRefreshKey((k) => k + 1), { cooldownMs: 4000 });

  // Load member stores for display names
  useEffect(() => {
    const loadMemberStores = async () => {
      try {
        const result = await getStoresByType(STORE_TYPES.MEMBER);
        if (result.success) {
          setMemberStores(result.stores);
        }
      } catch (error) {
        console.error('Error loading member stores:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMemberStores();
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    const cartRef = doc(db, "carts", user.uid);
    const u1 = onSnapshot(cartRef, (s) =>
      setCart(s.exists() ? s.data() || { items: [] } : { items: [] })
    );
    const u2 = onSnapshot(doc(db, "settings", "payments"), (s) =>
      setSettings(s.data() || null)
    );
    const u3 = onSnapshot(doc(db, "settings", "delivery"), (s) =>
      setDeliverySettings(s.data() || null)
    );
    const u4 = onSnapshot(doc(db, "settings", "qr"), (s) =>
      setQr(s.data() || null)
    );
    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }, [user?.uid, refreshKey]);

  // Get store key from URL parameter
  const storeKey = searchParams.get('store') || 'cooperative';

  // Helper function to get store display name
  function getStoreDisplayName(storeKey) {
    if (storeKey === 'cooperative') {
      return 'Cooperative Store';
    } else if (storeKey === 'legacy') {
      return 'Legacy Items';
    } else if (storeKey.startsWith('member_')) {
      const storeId = storeKey.replace('member_', '');
      const store = memberStores.find(s => s.id === storeId);
      return store ? store.name : `Member Store (${storeId})`;
    } else if (storeKey.startsWith('establishment_')) {
      const storeId = storeKey.replace('establishment_', '');
      return `Establishment Store (${storeId})`;
    }
    return storeKey;
  }

  // Prefill shipping info from user profile if available
  useEffect(() => {
    const s = profile?.shipping || profile?.preferredShipping || null;
    if (!s) return;
    if (typeof s.address === "string" && !shipAddress) setShipAddress(s.address);
    if (typeof s.phone === "string" && !shipPhone) setShipPhone(s.phone);
    const lat = s?.location?.lat; const lng = s?.location?.lng;
    if ((lat != null && lng != null) && (shipLat == null && shipLng == null)) {
      setShipLat(lat); setShipLng(lng);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.shipping, profile?.preferredShipping]);



  // Get store-specific cart items
  const currentItems = useMemo(() => {
    // First try to get from store-specific cart
    const storeItems = getStoreItems(storeKey);
    if (storeItems && storeItems.length > 0) {
      return storeItems;
    }
    
    // Fallback to legacy cart items if no store-specific items
    return Array.isArray(cart.items) ? cart.items : [];
  }, [storeKey, getStoreItems, cart.items]);

  // Get store-specific totals
  const storeTotals = useMemo(() => {
    return getStoreTotals(storeKey);
  }, [storeKey, getStoreTotals]);

  // Redirect to cart if no items in the specified store
  useEffect(() => {
    if (!loading && currentItems.length === 0) {
      notify('No items in cart for this store. Redirecting to cart.', 'info');
      nav('/cart');
    }
  }, [loading, currentItems.length, nav, notify]);

  // State to store enhanced cart items with product data
  const [enhancedItems, setEnhancedItems] = useState([]);

  // Fetch missing product data for cart items
  useEffect(() => {
    if (!currentItems.length) return;
    
    const fetchProductData = async () => {
      const enhanced = [];
      
      for (const item of currentItems) {
        // Try to get product ID from multiple possible fields
        const productId = item.productId || item.id;
        
        if (item.categories && Array.isArray(item.categories) && item.categories.length > 0) {
          // Item already has categories array, use as is
          enhanced.push(item);
        } else {
          try {
            // Fetch product data from database to get categories
            const productRef = doc(db, "products", productId);
            
            console.log('Fetching product data for:', productId, 'from products collection');
            const productSnap = await getDoc(productRef);
            
            if (productSnap.exists()) {
              const productData = productSnap.data();
              console.log('Product data found:', {
                name: productData.name,
                kgPerUnit: productData.kgPerUnit,
                categories: productData.categories
              });
              
              const enhancedItem = {
                ...item,
                categories: productData.categories || [],
                quantityStep: productData.quantityStep || 0.25,
                kgPerUnit: productData.kgPerUnit || 0
              };
              
              enhanced.push(enhancedItem);
            } else {
              console.log('Product not found in products collection, trying store-specific collections...');
              
              // Try store-specific collections (same order as backend)
              const storeCollections = [
                `cooperative_products_${item.storeId || 'eP7vVqzAHkZIH3NHNjsc'}`,
                'cooperative_products',
                `member_products_${item.storeId || 'cooperative'}`,
                `establishment_products_${item.storeId || 'cooperative'}`
              ];
              
              let found = false;
              for (const collectionName of storeCollections) {
                try {
                  const storeProductRef = doc(db, collectionName, productId);
                  const storeProductSnap = await getDoc(storeProductRef);
                  
                  if (storeProductSnap.exists()) {
                    const storeProductData = storeProductSnap.data();
                    console.log('Product found in', collectionName, ':', {
                      name: storeProductData.name,
                      kgPerUnit: storeProductData.kgPerUnit,
                      categories: storeProductData.categories
                    });
                    
                    const enhancedItem = {
                      ...item,
                      categories: storeProductData.categories || [],
                      quantityStep: storeProductData.quantityStep || 0.25,
                      kgPerUnit: storeProductData.kgPerUnit || 0
                    };
                    
                    enhanced.push(enhancedItem);
                    found = true;
                    break;
                  }
                } catch (error) {
                  console.log('Error checking', collectionName, ':', error);
                }
              }
              
              if (!found) {
                console.log('Product not found in any collection, using defaults');
                // Product not found, use item as is with default values
                enhanced.push({
                  ...item,
                  categories: [],
                  quantityStep: 0.25,
                  kgPerUnit: 0
                });
              }
            }
          } catch (error) {
            // On error, use item as is with default values
            enhanced.push({
              ...item,
              categories: [],
              quantityStep: 0.25,
              kgPerUnit: 0
            });
          }
        }
      }
      
      setEnhancedItems(enhanced);
    };
    
    fetchProductData();
  }, [currentItems]);

  const subtotal = useMemo(
    () => storeTotals.subtotal,
    [storeTotals.subtotal]
  );

  // Define these variables first before using them in useMemo
  const isManual = ["bank_transfer", "gcash_manual", "static_qr"].includes(method);
  const isCOD = method === "cod";
  const manualDelivery = isManual && deliveryMode === "delivery";

  // Compute distance when delivery is active and both coords available
  const isDeliveryOrder = isCOD || manualDelivery || deliveryMode === "delivery";
  useEffect(() => {
    setDistanceErr("");
    // Reset when switching off delivery or missing coords/config
    if (!isDeliveryOrder) { setDistanceAddon(null); return; }
    const cfg = deliverySettings?.settings || deliverySettings || {};
    const sLat = Number(cfg?.storeLocation?.lat || cfg?.storeLocation?.latitude || 0);
    const sLng = Number(cfg?.storeLocation?.lng || cfg?.storeLocation?.longitude || 0);
    const cLat = shipLat == null ? null : Number(shipLat);
    const cLng = shipLng == null ? null : Number(shipLng);

    if (!sLat || !sLng || cLat == null || cLng == null) { setDistanceAddon(null); return; }

    // Debounce rapid changes
    const t = setTimeout(async () => {
      try {
        setDistanceBusy(true);
        const fn = httpsCallable(functions, "computeDeliveryDistance");
        const res = await fn({
          store: { lat: sLat, lng: sLng },
          customer: { lat: cLat, lng: cLng }
        });
        const data = res?.data || {};
        // Normalize minimal fields used downstream
        setDistanceAddon({
          distanceKm: Number(data.distanceKm || 0),
          distanceSource: String(data.distanceSource || ""),
          extraKm: Number(data.extraKm || 0),
          chargeableKm: Number(data.chargeableKm || 0),
          addonPhp: Number(data.addonPhp || 0),
          policy: data.policy || null,
          store: { lat: sLat, lng: sLng },
          customer: { lat: cLat, lng: cLng }
        });
      } catch (e) {
        console.warn("distance compute failed", e);
        setDistanceErr(typeof e?.message === "string" ? e.message : "Failed to compute distance");
        setDistanceAddon(null);
      } finally {
        setDistanceBusy(false);
      }
    }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeliveryOrder, deliverySettings, shipLat, shipLng]);

  // Helper function to determine if a category is grocery
  const isGroceryCategory = (categoryName) => {
    if (!categoryName) return false;
    const cat = categoryName.toLowerCase().trim();
    
    // Grocery categories based on store sections - only packaged/dry goods
    const groceryCategories = [
      'rice', 'dairy products', 'dairy', 'milk', 'cheese', 'yogurt', 'butter',
      'bread', 'pasta', 'noodles', 'cereal', 'oatmeal', 'flour', 'sugar',
      'cooking oil', 'vinegar', 'soy sauce', 'fish sauce', 'salt', 'pepper',
      'canned goods', 'beans', 'lentils', 'chickpeas', 'peanuts', 'cashews',
      'chips', 'crackers', 'biscuits', 'chocolate', 'candy', 'jam', 'honey',
      'coffee', 'tea', 'juice', 'soda', 'water', 'snacks',
      'household', 'cleaning', 'personal care', 'hygiene', 'toiletries'
    ];
    
    // Fresh produce and spices are NOT grocery (they use quantityStep)
    const nonGroceryCategories = [
      'gulay lowland', 'gulay', 'vegetables', 'fresh produce', 'spices', 'condiments/spices',
      'fruits', 'meat', 'fish', 'seafood', 'fresh fish', 'live fish', 'dressed fish',
      'herbs', 'ginger', 'luya', 'sili', 'lapu-lapu', 'tilapia', 'bangus', 'tuna'
    ];
    
    // Check if it's explicitly non-grocery first
    if (nonGroceryCategories.some(nonGroceryCat => cat.includes(nonGroceryCat) || nonGroceryCat.includes(cat))) {
      return false;
    }
    
    // Then check if it's grocery
    return groceryCategories.some(groceryCat => cat.includes(groceryCat) || groceryCat.includes(cat));
  };

  // Calculate order weight using kgPerUnit field
  const orderWeight = useMemo(() => {
    return enhancedItems.reduce((total, item) => {
      let itemWeight = 0;
      
      // Debug logging
      console.log('Main weight calculation:', {
        name: item.name,
        qty: item.qty,
        kgPerUnit: item.kgPerUnit,
        categories: item.categories
      });
      
      // Use kgPerUnit if available, otherwise fall back to category-based logic
      if (item.kgPerUnit && item.kgPerUnit > 0) {
        // Use the new kgPerUnit field
        itemWeight = item.kgPerUnit * Number(item.qty || 0);
        console.log('Main calc using kgPerUnit:', item.kgPerUnit, 'Item weight:', itemWeight);
      } else {
        // Fallback to old category-based logic for existing products
        const isGrocery = item.categories && 
          (Array.isArray(item.categories) 
            ? item.categories.some(cat => isGroceryCategory(cat))
            : isGroceryCategory(item.categories)
          );
        
        console.log('Main calc using fallback, isGrocery:', isGrocery);
        
        if (isGrocery) {
          // Grocery items: 1 quantity = 0.2kg
          itemWeight = 0.2 * Number(item.qty || 0);
        } else {
          // Non-grocery items: quantity IS the weight
          itemWeight = Number(item.qty || 0);
        }
        console.log('Main calc item weight:', itemWeight);
      }
      
      return total + itemWeight;
    }, 0);
  }, [enhancedItems]);

  // Calculate weight-based fees - ONLY for delivery orders (not pickup)
  const weightBasedFee = useMemo(() => {
    // Only apply weight fees for delivery orders (COD, manual delivery, or deliveryMode is "delivery")
    const isDeliveryOrder = isCOD || manualDelivery || deliveryMode === "delivery";
    if (!isDeliveryOrder) {
      return 0;
    }
    
    // Get delivery settings from database - handle both nested and flat structures
    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    
    if (!deliveryConfig.weightBasedFees?.enabled) {
      return 0;
    }
    
    const weightConfig = deliveryConfig.weightBasedFees;
    const baseWeight = Number(weightConfig.baseWeight);
    const overweightThreshold = Number(weightConfig.overweightThreshold);
    const overweightFee = Number(weightConfig.overweightFee);
    const maxWeight = Number(weightConfig.maxWeight);
    const maxWeightFee = Number(weightConfig.maxWeightFee);
    
    // Validate that required weight fee settings exist
    if (!overweightThreshold || !overweightFee) {
      return 0;
    }
    
    // Check if order exceeds base weight
    if (orderWeight > overweightThreshold) {
      const overweightKg = orderWeight - overweightThreshold;
      let fee = overweightKg * overweightFee;
      
      // Cap at maximum weight fee
      if (fee > maxWeightFee) {
        fee = maxWeightFee;
      }
      
      return fee;
    }
    
    return 0;
  }, [orderWeight, deliverySettings, isCOD, manualDelivery, deliveryMode]);

  // Distance add-on amount (separate line item) - declare BEFORE using it elsewhere
  const distanceAddonPhp = useMemo(() => {
    const _isDeliveryOrder = isCOD || manualDelivery || deliveryMode === "delivery";
    if (!_isDeliveryOrder) return 0;
    return Number(distanceAddon?.addonPhp || 0);
  }, [isCOD, manualDelivery, deliveryMode, distanceAddon?.addonPhp]);

  // Calculate rider compensation
  const riderCompensation = useMemo(() => {
    const isDeliveryOrder = isCOD || manualDelivery || deliveryMode === "delivery";
    if (!isDeliveryOrder) return 0; // Only for delivery orders

    const flatRate = 45; // Base payment for riders
    const weightFeePercentage = 0.9; // 90% of weight fee goes to riders

    const weightFeeForRider = weightBasedFee * weightFeePercentage;
    // Policy: Distance add-on is given to the rider 100%
    const distanceAddonForRider = Number(distanceAddonPhp || 0);
    const totalRiderTake = flatRate + weightFeeForRider + distanceAddonForRider;

    return {
      flatRate,
      weightFeeForRider,
      distanceAddonForRider,
      total: totalRiderTake
    };
  }, [weightBasedFee, distanceAddonPhp, isCOD, manualDelivery, deliveryMode]);

  // Calculate delivery fee
  const deliveryFee = useMemo(() => {
    // Only apply delivery fee for delivery orders (COD, manual delivery, or deliveryMode is "delivery")
  const _isDeliveryOrder = isCOD || manualDelivery || deliveryMode === "delivery";
  if (!_isDeliveryOrder) return 0;
    if (!isDeliveryOrder) return 0;
    
    // Get delivery settings from database - handle both nested and flat structures
    const deliveryConfig = deliverySettings?.settings || deliverySettings || {};
    
    // Check if delivery is enabled
    if (deliveryConfig.enabled === false) return 0;
    
    // Check free delivery threshold - use the actual setting from database
    const freeThreshold = deliveryConfig.freeDeliveryThreshold;
    
    if (freeThreshold && subtotal >= freeThreshold) {
      return 0;
    }
    
    // Calculate fee based on delivery fee type
    if (deliveryConfig.deliveryFeeType === 'fixed') {
      const fee = deliveryConfig.fixedFee;
      if (fee && fee > 0) {
  return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'distance') {
      // For distance-based, use base fee for now
      const fee = deliveryConfig.baseFee;
      if (fee && fee > 0) {
  return fee;
      } else {
        return 0;
      }
    } else if (deliveryConfig.deliveryFeeType === 'zones') {
      // For zones-based delivery, calculate based on delivery zones
      
      if (!deliveryConfig.deliveryZones || !Array.isArray(deliveryConfig.deliveryZones)) {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
      
      // For now, use the first zone fee as default
      // In the future, this could be enhanced to calculate actual distance-based zone fees
      const defaultZoneFee = deliveryConfig.deliveryZones[0]?.fee;
      if (defaultZoneFee && defaultZoneFee > 0) {
  return defaultZoneFee;
      } else {
        const fee = deliveryConfig.baseFee;
        if (fee && fee > 0) {
          return fee;
        }
        return 0;
      }
    }
    // No delivery fee type configured
    return 0;
  }, [subtotal, isCOD, manualDelivery, deliveryMode, deliverySettings]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
  let total = subtotal + deliveryFee + weightBasedFee + distanceAddonPhp;
    
    // Apply discounts
    appliedDiscounts.forEach(discount => {
      if (discount.discountType === 'percentage') {
        total -= (subtotal * discount.discountAmount) / 100;
      } else if (discount.discountType === 'fixed') {
        total -= discount.discountAmount;
      } else if (discount.discountType === 'free_shipping') {
        total -= deliveryFee;
      }
    });
    
    return Math.max(0, total); // Ensure total doesn't go below 0
  }, [subtotal, deliveryFee, weightBasedFee, distanceAddonPhp, appliedDiscounts]);

  // Handle discount application/removal
  const handleDiscountApplied = (discount, isRemoval = false) => {
    if (isRemoval) {
      setAppliedDiscounts(prev => prev.filter(d => 
        !(d.type === discount.type && d.code === discount.code)
      ));
    } else {
      setAppliedDiscounts(prev => [...prev, discount]);
    }
  };

  const manualMethods = useMemo(() => {
    const base = Array.isArray(settings?.allowedManualMethods)
      ? settings.allowedManualMethods
      : ["bank_transfer", "gcash_manual", "static_qr"];
    return base.filter((m) => MANUAL_METHOD_LABELS[m]);
  }, [settings]);

  // Derive dynamic payment details from settings with safe fallbacks
  const paymentDetails = useMemo(() => {
    const s = settings || {};
    const bank = {
      name: s?.bank?.name || s?.bankName || s?.accounts?.bank?.name || "RCBC",
      accountNumber: s?.bank?.accountNumber || s?.bankAccount || s?.accounts?.bank?.account || "9006303426",
      accountName: s?.bank?.accountName || s?.bankAccountName || s?.accounts?.bank?.accountName || "Puerto Princesa Agriculture Cooperative",
      instructions: s?.instructionsBank || "Bank name:\nAccount name:\nAccount number:\nNote: Include your name and reference number.",
    };
    const gcash = {
      name: s?.gcash?.name || s?.gcashName || s?.accounts?.gcash?.name || "Dexter Acantilado",
  number: s?.gcash?.number || s?.gcashNumber || s?.accounts?.gcash?.number || "095046866668",
      instructions: s?.instructionsGCash || "GCash name:\nGCash number:\nNote: Include your name and reference number.",
    };
    return { bank, gcash };
  }, [settings]);

  // Small component: manual payment checklist
  const ManualChecklist = ({ channel }) => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mt-2">
      <div className="text-sm font-medium text-amber-900 mb-1">Payment checklist</div>
      <ul className="list-disc pl-5 text-xs text-amber-800 space-y-1">
        <li>Send payment via {channel === "bank_transfer" ? "your bank app" : "GCash"} to the account shown.</li>
        <li>Note or copy the Reference No. shown in your receipt.</li>
        <li>Upload a clear proof/screenshot below and enter the Reference No.</li>
        <li>Click Place Order to submit for verification.</li>
      </ul>
    </div>
  );

  // Optional toggle from settings to enable COD
  const allowCOD = settings?.allowCOD === true;
  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

  // --- Basic PH mobile helpers ---
  function normalizePHPhone(input) {
    if (!input) return { normalized: "", ok: false };
    let v = String(input).trim();
    // Remove common separators
    v = v.replace(/[()\-\s]/g, "");
    // Convert +63 or 63 prefix to 0
    if (v.startsWith("+63")) v = "0" + v.slice(3);
    else if (v.startsWith("63")) v = "0" + v.slice(2);
    // Keep digits only
    v = v.replace(/\D/g, "");
    const ok = v.length === 11 && v.startsWith("09");
    return { normalized: v, ok };
  }
  function formatPHPhone(input) {
    const digits = String(input || "").replace(/\D/g, "");
    if (!digits) return "";
    // Show as 09xx xxx xxxx while typing when starting with 09
    if (digits.startsWith("09")) {
      const p1 = digits.slice(0, 4);
      const p2 = digits.slice(4, 7);
      const p3 = digits.slice(7, 11);
      return [p1, p2, p3].filter(Boolean).join(" ");
    }
    return digits;
  }

  // Reset manual-only fields when switching methods
  useEffect(() => {
    setUploadProgress(0);
    setFileError("");
    setErrorMsg("");
    if (!isManual) {
      setReferenceNo("");
      setFile(null);
    }
    // Clear shipping only when neither COD nor manual-delivery is active
    if (!isCOD && !manualDelivery) {
      setShipAddress("");
      setShipPhone("");
      setShipPhoneErr("");
      setShipLat(null);
      setShipLng(null);
    }
  }, [isManual, isCOD, manualDelivery]);

  // Automatically set delivery mode to "delivery" when COD is selected
  useEffect(() => {
    if (isCOD && deliveryMode !== "delivery") {
      setDeliveryMode("delivery");
    }
  }, [isCOD, deliveryMode]);

  function validateFile(f) {
    if (!f) return false;
    const okTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    const ok = okTypes.includes(f.type);
    return ok; // No size limit - we'll compress images to 1MB
  }

  // Compress image to 1MB or less
  async function compressImage(file, maxSizeMB = 1) {
    if (file.type === "application/pdf") {
      return file; // Don't compress PDFs
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    // If file is already small enough, return as is
    if (file.size <= maxSizeBytes) {
      return file;
    }

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions to reduce file size
        let { width, height } = img;
        const maxDimension = 1920; // Max width/height
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image with new dimensions
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality levels to get under 1MB
        const tryCompress = (quality) => {
          canvas.toBlob((blob) => {
            if (!blob) {
              resolve(file); // Fallback to original
              return;
            }
            
            if (blob.size <= maxSizeBytes || quality <= 0.1) {
              // Create a new file with the compressed blob
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              // Try with lower quality
              tryCompress(quality - 0.1);
            }
          }, file.type, quality);
        };
        
        // Start with 0.8 quality
        tryCompress(0.8);
      };
      
      img.onerror = () => resolve(file); // Fallback to original on error
      img.src = URL.createObjectURL(file);
    });
  }

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!method) return false;
    if (isManual) {
      const valid = referenceNo.trim() && file && validateFile(file);
      if (!valid) return false;
      if (manualDelivery) {
        const { ok } = normalizePHPhone(shipPhone);
        return Boolean(shipAddress.trim() && ok);
      }
      return true;
    }
    if (isCOD) {
      const { ok } = normalizePHPhone(shipPhone);
      return Boolean(shipAddress.trim() && ok);
    }
    return true;
  }, [busy, method, isManual, isCOD, manualDelivery, referenceNo, file, shipAddress, shipPhone]);

  async function onSubmit(e) {
    e.preventDefault();
    setErrorMsg("");
    
    // Check if user is signed in
    if (!user?.uid) {
      setShowSignUpModal(true);
      return;
    }
    
    if ((currentItems || []).length === 0) { setErrorMsg("Cart is empty."); return; }
    if (!method) { setErrorMsg("Choose a payment method."); return; }

    // For manual methods, validate BEFORE creating any records
    if (isManual) {
      if (!referenceNo.trim()) {
        setErrorMsg("Reference No is required for this payment method.");
        return;
      }
      if (!file || !validateFile(file)) {
        setErrorMsg("Valid proof file is required (JPG/PNG/WEBP/PDF).");
        return;
      }
      if (manualDelivery) {
        if (!shipAddress.trim()) { setErrorMsg("Address is required for delivery."); return; }
        const ph = normalizePHPhone(shipPhone);
        if (!ph.ok) { setErrorMsg("Enter a valid PH mobile number (09xxxxxxxxx)."); return; }
      }
    }
    // For COD, require address and phone
    if (isCOD) {
      if (!shipAddress.trim()) { setErrorMsg("Address is required for COD."); return; }
      const ph = normalizePHPhone(shipPhone);
      if (!ph.ok) { setErrorMsg("Enter a valid PH mobile number (09xxxxxxxxx)."); return; }
    }

    setBusy(true);
  let manualProofPath = null;
  try {
      // Determine store information from storeKey instead of relying on cart items
      const getStoreInfoFromKey = (key) => {
        if (key === 'cooperative') {
          return { storeType: 'cooperative', storeId: 'eP7vVqzAHkZIH3NHNjsc' };
        } else if (key.startsWith('member_')) {
          return { storeType: 'member', storeId: key.replace('member_', '') };
        } else if (key.startsWith('establishment_')) {
          return { storeType: 'establishment', storeId: key.replace('establishment_', '') };
        }
        // Fallback to cooperative
        return { storeType: 'cooperative', storeId: 'eP7vVqzAHkZIH3NHNjsc' };
      };
      
      const storeInfo = getStoreInfoFromKey(storeKey);
      
      const cleanItems = (currentItems || []).map((it) => ({
        productId: it.productId || it.id, // support both cart shapes
        name: it.name,
        price: Number(it.price),
        qty: Number(it.qty),
        storeType: storeInfo.storeType, // Use correct store type from storeKey
        storeId: storeInfo.storeId, // Use correct store ID from storeKey
      }));

      let proofURL = "";
      if (isManual) {
        // Compress image to 1MB before upload
        const compressedFile = await compressImage(file, 1);
        
        // Upload proof first to an allowed folder per storage.rules (paidUpProof)
        const ext = (compressedFile.name.split(".").pop() || "").toLowerCase();
        const safeExt = ["jpg", "jpeg", "png", "webp", "pdf"].includes(ext)
          ? ext
          : compressedFile.type === "application/pdf"
          ? "pdf"
          : "jpg";
        // Use a time-based name to avoid collisions (function will create payment doc)
        const filename = `${Date.now()}.${safeExt}`;
        manualProofPath = `members/${user.uid}/paidUpProof/${filename}`;
        const sref = storageRef(storage, manualProofPath);
        const task = uploadBytesResumable(sref, compressedFile, { contentType: compressedFile.type });
        await new Promise((res, rej) =>
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              setUploadProgress(pct);
            },
            rej,
            res
          )
        );
        proofURL = await getDownloadURL(sref);
      }

      // Call backend to place order atomically
      const callable = httpsCallable(functions, "placeOrder");
      const shippingPayload = { address: shipAddress.trim(), phone: normalizePHPhone(shipPhone).normalized, location: (shipLat!=null && shipLng!=null) ? { lat: shipLat, lng: shipLng } : null };
      // Compute method for storage: cod | pickup | prepaid
      const computedMethod = isCOD ? "cod" : (isManual ? (deliveryMode === "delivery" ? "prepaid" : "pickup") : method);
      const storeName = getStoreDisplayName(storeKey);
      
      // Get affiliate code from localStorage if available
      // Get affiliate code using the new tracking system
      const affiliateCode = localStorage.getItem('affiliateCode');
      let parsedAffiliateCode = null;
      
      
      try {
        if (affiliateCode) {
          const affiliateData = JSON.parse(affiliateCode);
          // Only use validated codes or codes that are less than 30 days old
          const isExpired = Date.now() - affiliateData.timestamp > (30 * 24 * 60 * 60 * 1000);
          
          
          if (!isExpired) {
            parsedAffiliateCode = affiliateData.code;
          }
        }
      } catch (error) {
        console.error('Error parsing affiliate code:', error);
        // Fallback to old format for backward compatibility
        parsedAffiliateCode = affiliateCode;
      }
      
      
      const payloadBase = { distanceAddon: (isCOD || manualDelivery) ? (distanceAddon || null) : null };
      const payload = isManual
        ? (manualDelivery
            ? { items: cleanItems, method: computedMethod, channel: method, referenceNo: referenceNo.trim(), proofURL, shipping: shippingPayload, deliveryMode, storeKey, storeType: storeInfo.storeType, storeId: storeInfo.storeId, storeName, affiliateCode: parsedAffiliateCode, ...payloadBase }
            : { items: cleanItems, method: computedMethod, channel: method, referenceNo: referenceNo.trim(), proofURL, deliveryMode, storeKey, storeType: storeInfo.storeType, storeId: storeInfo.storeId, storeName, affiliateCode: parsedAffiliateCode, ...payloadBase })
        : isCOD
        ? { items: cleanItems, method: computedMethod, shipping: shippingPayload, deliveryMode: "delivery", storeKey, storeType: storeInfo.storeType, storeId: storeInfo.storeId, storeName, affiliateCode: parsedAffiliateCode, ...payloadBase }
        : { items: cleanItems, method: computedMethod, deliveryMode: "pickup", storeKey, storeType: storeInfo.storeType, storeId: storeInfo.storeId, storeName, affiliateCode: parsedAffiliateCode, ...payloadBase };
      

      
      const res = await callable(payload);
      const { orderId, paymentId } = (res?.data || {});

      // Clear store-specific cart state; server clears Firestore cart within the txn
      try { clear(storeKey); } catch {}
      setCart((c) => ({ ...c, items: [] }));

      // Save shipping info for future orders when applicable
      try {
        if (user?.uid && (isCOD || manualDelivery)) {
          await setDoc(doc(db, "users", user.uid), { shipping: shippingPayload }, { merge: true });
        }
      } catch {}

      if (isManual) {
        if (manualDelivery) {
          notify("Order placed and paid. We'll deliver soon.", { type: "success" });
        } else {
          notify("Order placed. Payment is pending verification.", { type: "success" });
        }
        // Do not redirect to payments; return user to store
        nav("/store");
      } else {
        if (method === "cod") {
          notify("Order placed. Please prepare exact cash upon delivery.", { type: "success" });
        } else {
          notify(`Order ${orderId || ""} placed.`, { type: "success" });
        }
        nav("/store");
      }
    } catch (e) {
      // Best-effort cleanup for orphaned proof files when manual flow fails after upload
      if (isManual && manualProofPath) {
        try { await deleteObject(storageRef(storage, manualProofPath)); } catch {}
      }
  const msg = e?.message || String(e);
  setErrorMsg(msg);
  notify(msg, { type: "error" });
    } finally {
      setBusy(false);
    }
  }

  if ((currentItems || []).length === 0) {
    return (
      <PageBackground
        image="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80"
        boxed={false}
        overlayClass="bg-white/85 backdrop-blur"
        edgeToEdge={true}
      >
        <div className="min-h-screen">
          <div className="card p-6">
            <div className="text-lg font-semibold mb-2">Your cart is empty</div>
            <button className="btn btn-primary" onClick={() => nav("/store")}>
              Go to Store
            </button>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <>
      <MobileBackButton to="/cart" />
      <PageBackground
        image="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80"
        boxed={false}
        overlayClass="bg-white/85 backdrop-blur"
        edgeToEdge={true}
      >
      <div className="min-h-screen">
        <div className="p-2 pb-32 sm:p-4 sm:pb-4">
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h1 className="text-2xl font-bold">Checkout</h1>
              <p className="text-sm text-gray-600">{getStoreDisplayName(storeKey)}</p>
            </div>
            <Link to="/cart">
              <div className="flex items-center gap-2 bg-brand-100 text-brand-700 px-3 py-1 rounded-full hover:bg-brand-200 transition-colors cursor-pointer">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                </svg>
                <span className="text-sm font-medium">{currentItems?.length || 0} items</span>
              </div>
            </Link>
          </div>

        <form id="checkout-form" className="card p-3 sm:p-4 space-y-3 sm:space-y-4" onSubmit={onSubmit} autoComplete="on">
          {/* Order summary */}
          {errorMsg && (
            <div className="p-3 rounded bg-rose-50 text-rose-700 text-sm border border-rose-200">
              {errorMsg}
            </div>
          )}
          
          {/* Sign up prompt for non-authenticated users */}
          {!user && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-blue-900 text-sm mb-1">Complete Your Order</h4>
                  <p className="text-blue-700 text-sm mb-3">
                    You need to create an account to complete your purchase. This will only take a minute!
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowSignUpModal(true)}
                    className="btn btn-primary btn-sm"
                  >
                    Sign Up Now
                  </button>
                </div>
              </div>
            </div>
          )}
          <div>
            <div className="text-xs text-ink/60 mb-3">Items ({currentItems?.length || 0})</div>
            <div className="space-y-3">
              {(currentItems || []).map((it) => {
                const img = it.image || it.imageUrl || it.thumbnail || "";
                const lineTotal = (Number(it.price || 0) * Number(it.qty || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                  <div key={it.productId || it.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {img ? (
                          <img src={img} alt="" className="h-16 w-16 object-cover rounded-lg border" loading="lazy" decoding="async" />
                        ) : (
                          <div className="h-16 w-16 rounded-lg border bg-gray-200 grid place-items-center text-gray-400 text-xs">No Img</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm leading-tight mb-1">{it.name}</div>
                        <div className="text-xs text-gray-600 mb-2">₱{it.price} each</div>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">Qty: {it.qty}</div>
                          <div className="text-sm font-semibold text-gray-900">₱{lineTotal}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink/60 mb-2">Method</div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              {manualMethods.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={
                    method === m
                      ? "btn btn-primary btn-sm w-full sm:w-auto"
                      : "btn btn-outline btn-sm w-full sm:w-auto"
                  }
                  onClick={() => !busy && setMethod(m)}
                  disabled={busy}
                >
                  {MANUAL_METHOD_LABELS[m]}
                </button>
              ))}
              {allowCOD && (
                <button
                  type="button"
                  className={
                    method === "cod"
                      ? "btn btn-primary btn-sm w-full sm:w-auto"
                      : "btn btn-outline btn-sm w-full sm:w-auto"
                  }
                  onClick={() => !busy && setMethod("cod")}
                  disabled={busy}
                >
                  {COD_LABEL}
                </button>
              )}
            </div>
          </div>

          {/* Manual method account/instructions (same content as Payments page) */}
          {(method === "bank_transfer" || method === "gcash_manual") && (
            <div className="space-y-3">

              
              {/* Bank Transfer Details */}
              {method === "bank_transfer" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-900 text-sm">Bank Details:</h4>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(paymentDetails.bank.accountNumber || ""));
                          notify("Account number copied to clipboard", { type: "success" });
                        } catch {
                          notify("Failed to copy", { type: "error" });
                        }
                      }}
                    >
                      Copy Account No
                    </button>
                  </div>
                  <div className="space-y-1 text-xs">
                    <p><span className="font-medium">Bank:</span> {paymentDetails.bank.name}</p>
                    <p><span className="font-medium">Account No.:</span> {paymentDetails.bank.accountNumber}</p>
                    <p><span className="font-medium">Account Name:</span> {paymentDetails.bank.accountName}</p>
                    {paymentDetails.bank.instructions && (
                      <pre className="whitespace-pre-wrap text-ink/70 mt-2">{paymentDetails.bank.instructions}</pre>
                    )}
                  </div>
                  <ManualChecklist channel="bank_transfer" />
                </div>
              )}
              
              {/* GCash Details */}
              {method === "gcash_manual" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-green-900 text-sm">GCash Details:</h4>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(paymentDetails.gcash.number || ""));
                          notify("Mobile number copied to clipboard", { type: "success" });
                        } catch {
                          notify("Failed to copy", { type: "error" });
                        }
                      }}
                    >
                      Copy Mobile No
                    </button>
                  </div>
                  <div className="space-y-1 text-xs">
                    <p><span className="font-medium">Mobile Number:</span> {paymentDetails.gcash.number}</p>
                    <p><span className="font-medium">Account Name:</span> {paymentDetails.gcash.name}</p>
                    {paymentDetails.gcash.instructions && (
                      <pre className="whitespace-pre-wrap text-ink/70 mt-2">{paymentDetails.gcash.instructions}</pre>
                    )}
                    <p className="text-xs text-green-700 mt-2">💡 Tip: Use the GCash QR code below for faster payment</p>
                  </div>
                  <ManualChecklist channel="gcash_manual" />
                </div>
              )}
            </div>
          )}

          {method === "static_qr" && (
            <div className="space-y-3">
              <p className="text-sm">
                Scan the QR, then upload your proof below.
              </p>
              <div className="flex gap-2">
                {qr?.static?.gcash?.imageUrl && (
                  <img
                    src={qr.static.gcash.imageUrl}
                    alt="GCash QR"
                    className="max-w-[200px] border rounded"
                  />
                )}
                {qr?.static?.bank?.imageUrl && (
                  <img
                    src={qr.static.bank.imageUrl}
                    alt="Bank QR"
                    className="max-w-[200px] border rounded"
                  />
                )}
              </div>
              {!qr?.static?.gcash?.imageUrl && !qr?.static?.bank?.imageUrl && (
                <div className="text-sm text-ink/60">
                  <p>No static QR set. For GCash payments, use:</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-green-900 text-sm">GCash Details:</h4>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(String(paymentDetails.gcash.number || ""));
                            notify("Mobile number copied to clipboard", { type: "success" });
                          } catch {
                            notify("Failed to copy", { type: "error" });
                          }
                        }}
                      >
                        Copy Mobile No
                      </button>
                    </div>
                    <div className="space-y-1 text-xs">
                      <p><span className="font-medium">Mobile Number:</span> {paymentDetails.gcash.number}</p>
                      <p><span className="font-medium">Account Name:</span> {paymentDetails.gcash.name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show manual inputs only when method is one of the manual ones */}
          {isManual && (
            <>
              {/* Pickup or Delivery toggle for manual payments */}
              <div className="border rounded p-2">
                <div className="text-xs text-ink/60 mb-1">Fulfillment</div>
                <div className="flex gap-2 mb-2">
                  <button type="button" className={deliveryMode === "pickup" ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"} onClick={() => setDeliveryMode("pickup")}>Pick up</button>
                  <button type="button" className={deliveryMode === "delivery" ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"} onClick={() => setDeliveryMode("delivery")}>For delivery</button>
                </div>
                {deliveryMode === "pickup" && (
                  <div className="text-sm text-ink/80">
                    Pickup location: <span className="font-medium">City Food Terminal, New Market Brgy. San Jose PPC, Palawan</span>
                    <div className="text-xs text-ink/60">Landmark: Near Palawan Pawnshop</div>
                    <div className="text-sm">Contact: <a href="tel:095046866668" className="underline">095046866668</a></div>
                  </div>
                )}
              </div>
              <label className="block">
                <div className="text-xs text-ink/60">Reference No</div>
                <input
                  className="input"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                />
              </label>
              <label className="block">
                <div className="text-xs text-ink/60">
                  Proof (JPG/PNG/WEBP/PDF - will be compressed to 1MB)
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf" capture
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    if (!f) { setFileError(""); return; }
                    if (!validateFile(f)) {
                      setFileError("Must be JPG/PNG/WEBP/PDF; large images are auto-compressed to ≤ 1MB.");
                    } else {
                      setFileError("");
                    }
                  }}
                />
                {fileError && <div className="text-sm text-rose-700 mt-1">{fileError}</div>}
              </label>
              {busy && uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full bg-gray-200 rounded h-2 overflow-hidden">
                  <div
                    className="bg-brand-600 h-2"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}

              {/* Manual delivery shipping inputs (same as COD) */}
              {deliveryMode === "delivery" && (
                <>
                  {/* Auto-fill from profile button */}
                  {!profileLoading && profile && (profile.address || profile.phone || profile.phoneNumber || 
                    (profile.shipping && (profile.shipping.address || profile.shipping.phone)) ||
                    profile.firstName || profile.lastName) && (
                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={() => {
                          // Try multiple possible field locations
                          if (profile.address) setShipAddress(profile.address);
                          if (profile.phone) setShipPhone(profile.phone);
                          if (profile.phoneNumber) setShipPhone(profile.phoneNumber);
                          
                          // Check shipping object
                          if (profile.shipping?.address) setShipAddress(profile.shipping.address);
                          if (profile.shipping?.phone) setShipPhone(profile.shipping.phone);
                          
                          // If we have name but no address, create a basic address
                          if (!profile.address && !profile.shipping?.address && profile.firstName && profile.lastName) {
                            setShipAddress(`${profile.firstName} ${profile.lastName}'s Address`);
                          }
                        }}
                        className="inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 transition-colors"
                        title="Use my saved address and phone number"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Use My Saved Info
                      </button>
                    </div>
                  )}
                  
                  <label className="block">
                    <div className="text-xs text-ink/60">Delivery Address</div>
                    <textarea className="w-full border rounded px-2 py-1" rows={3} value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder="House/Unit, Street, Barangay, City" autoComplete="street-address" enterKeyHint="next" />
                  </label>
                  <label className="block">
                    <div className="text-xs text-ink/60">Contact Phone</div>
                    <input
                      className="input"
                      type="tel"
                      inputMode="numeric" autoComplete="tel" enterKeyHint="done"
                      value={shipPhone}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setShipPhone(raw);
                        if (!raw.trim()) { setShipPhoneErr(""); return; }
                        const { ok } = normalizePHPhone(raw);
                        setShipPhoneErr(ok ? "" : "Enter a valid PH mobile (09xxxxxxxxx)");
                      }}
                      placeholder="09xx xxx xxxx"
                      maxLength={14}
                    />
                    {shipPhoneErr && <div className="text-xs text-rose-700 mt-1">{shipPhoneErr}</div>}
                  </label>
                  <div className="space-y-2">
                    <div className="text-xs text-ink/60">Pin your location (optional)</div>
                    {mapsKey ? (
                      <MapPicker
                        lat={shipLat ?? undefined}
                        lng={shipLng ?? undefined}
                        apiKey={mapsKey}
                        onChange={(la, ln) => { setShipLat(la); setShipLng(ln); }}
                      />
                    ) : (
                      <div className="rounded border bg-gray-50 p-2 text-xs text-ink/60">
                        To enable map, add VITE_GOOGLE_MAPS_API_KEY to your environment.
                        <button
                          type="button"
                          className="btn btn-xs btn-outline ml-2"
                          onClick={() => {
                            if (!navigator.geolocation) return;
                            navigator.geolocation.getCurrentPosition(
                              (pos) => { setShipLat(pos.coords.latitude); setShipLng(pos.coords.longitude); },
                              () => {}
                            );
                          }}
                        >Use my current location</button>
                        {shipLat != null && shipLng != null && (
                          <div className="mt-1">Pinned: {shipLat.toFixed(5)}, {shipLng.toFixed(5)}</div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* COD shipping inputs */}
          {isCOD && (
            <>
              {/* Auto-fill from profile button */}
              {!profileLoading && profile && (profile.address || profile.phone || profile.phoneNumber || 
                (profile.shipping && (profile.shipping.address || profile.shipping.phone)) ||
                profile.firstName || profile.lastName) && (
                <div className="mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Try multiple possible field locations
                      if (profile.address) setShipAddress(profile.address);
                      if (profile.phone) setShipPhone(profile.phone);
                      if (profile.phoneNumber) setShipPhone(profile.phoneNumber);
                      
                      // Check shipping object
                      if (profile.shipping?.address) setShipAddress(profile.shipping.address);
                      if (profile.shipping?.phone) setShipPhone(profile.shipping.phone);
                      
                      // If we have name but no address, create a basic address
                      if (!profile.address && !profile.shipping?.address && profile.firstName && profile.lastName) {
                        setShipAddress(`${profile.firstName} ${profile.lastName}'s Address`);
                      }
                    }}
                    className="inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 transition-colors"
                    title="Use my saved address and phone number"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Use My Saved Info
                </button>
                </div>
              )}
              
              <label className="block">
                <div className="text-xs text-ink/60">Delivery Address</div>
                <textarea className="w-full border rounded px-2 py-1" rows={3} value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder="House/Unit, Street, Barangay, City" autoComplete="street-address" enterKeyHint="next" />
              </label>
              <label className="block">
                <div className="text-xs text-ink/60">Contact Phone</div>
                <input
                  className="input"
                  type="tel"
                  inputMode="numeric" autoComplete="tel" enterKeyHint="done"
                  value={shipPhone}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setShipPhone(raw);
                    if (!raw.trim()) { setShipPhoneErr(""); return; }
                    const { ok } = normalizePHPhone(raw);
                    setShipPhoneErr(ok ? "" : "Enter a valid PH mobile (09xxxxxxxxx)");
                  }}
                  placeholder="09xx xxx xxxx"
                  maxLength={14}
                />
                {shipPhoneErr && <div className="text-xs text-rose-700 mt-1">{shipPhoneErr}</div>}
              </label>
              <div className="space-y-2">
                <div className="text-xs text-ink/60">Pin your location (optional)</div>
        {mapsKey ? (
                  <MapPicker
                    lat={shipLat ?? undefined}
                    lng={shipLng ?? undefined}
          apiKey={mapsKey}
          onChange={(la, ln) => { setShipLat(la); setShipLng(ln); }}
                  />
                ) : (
                  <div className="rounded border bg-gray-50 p-2 text-xs text-ink/60">
                    To enable map, add VITE_GOOGLE_MAPS_API_KEY to your environment.
                    <button
                      type="button"
                      className="btn btn-xs btn-outline ml-2"
                      onClick={() => {
                        if (!navigator.geolocation) return;
                        navigator.geolocation.getCurrentPosition(
                          (pos) => { setShipLat(pos.coords.latitude); setShipLng(pos.coords.longitude); },
                          () => {}
                        );
                      }}
                    >Use my current location</button>
                    {shipLat != null && shipLng != null && (
                      <div className="mt-1">Pinned: {shipLat.toFixed(5)}, {shipLng.toFixed(5)}</div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Discount Input */}
          <DiscountInput
            subtotal={subtotal}
            onDiscountApplied={handleDiscountApplied}
            appliedDiscounts={appliedDiscounts}
          />

          {/* Discounts Applied */}
          {appliedDiscounts.length > 0 && (
            <div className="space-y-2">
              {appliedDiscounts.map((discount, index) => (
                <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <div className="text-sm text-green-800">
                    {discount.code} - {discount.description}
                  </div>
                  <div className="text-sm font-medium text-green-800">
                    {discount.discountType === 'percentage' && `-${discount.discountAmount}%`}
                    {discount.discountType === 'fixed' && `-₱${discount.discountAmount}`}
                    {discount.discountType === 'free_shipping' && 'Free shipping'}
                  </div>
                </div>
              ))}
            </div>
          )}

                     {/* Order Weight Info */}
           {(isCOD || manualDelivery) && (
             <>
               <div className="flex items-center justify-between text-sm text-ink/60">
                 <div>Total Order Weight</div>
                 <div>{orderWeight.toFixed(2)} kg</div>
               </div>
               
                               {/* Weight Breakdown for each item */}
                <div className="text-xs text-ink/50 space-y-1">
                  {enhancedItems.map((item) => {
                   let totalItemWeight, weightDescription;
                   
                   // Debug logging
                   console.log('Item weight calculation:', {
                     name: item.name,
                     qty: item.qty,
                     kgPerUnit: item.kgPerUnit,
                     categories: item.categories
                   });
                   
                   // Use kgPerUnit if available, otherwise fall back to category-based logic
                   if (item.kgPerUnit && item.kgPerUnit > 0) {
                     // Use the new kgPerUnit field
                     totalItemWeight = item.kgPerUnit * Number(item.qty || 0);
                     weightDescription = ` (${item.kgPerUnit}kg/unit)`;
                     console.log('Using kgPerUnit:', item.kgPerUnit, 'Total:', totalItemWeight);
                   } else {
                     // Fallback to old category-based logic for existing products
                     const isGrocery = item.categories && 
                       (Array.isArray(item.categories) 
                         ? item.categories.some(cat => isGroceryCategory(cat))
                         : isGroceryCategory(item.categories)
                       );
                     
                     console.log('Using fallback logic, isGrocery:', isGrocery);
                     
                     if (isGrocery) {
                       totalItemWeight = 0.2 * Number(item.qty || 0);
                       weightDescription = ' (grocery: 0.2kg/unit)';
                     } else {
                       // Non-grocery: quantity IS the weight
                       totalItemWeight = Number(item.qty || 0);
                       weightDescription = ' (as-is)';
                     }
                   }
                   
                   return (
                     <div key={item.id || item.productId} className="flex justify-between">
                       <span>{item.name} ({item.qty}×)</span>
                       <span>
                         {totalItemWeight.toFixed(2)}kg 
                         {weightDescription}
                       </span>
                     </div>
                   );
                 })}
               </div>
             </>
           )}

           <div className="flex items-center justify-between border-t pt-3">
             <div className="text-lg font-semibold">Subtotal</div>
             <div className="text-lg font-bold">
               ₱
               {Number(subtotal).toLocaleString(undefined, {
                 minimumFractionDigits: 2,
                 maximumFractionDigits: 2,
               })}
             </div>
           </div>

          {/* Delivery Fee */}
          {(isCOD || manualDelivery) && deliveryFee > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-ink/70">Delivery Fee</div>
              <div className="text-sm font-medium">
                ₱{deliveryFee.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          )}

          {/* Distance Add-on Line */}
          {(isCOD || manualDelivery) && distanceAddon && distanceAddon.addonPhp > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-ink/70">
                Distance add-on ({Number(distanceAddon.distanceKm).toFixed(2)} km; first {(distanceAddon?.policy?.baseFreeKm ?? 3)} km free)
                {distanceBusy && <span className="ml-2 text-xs text-ink/50">recomputing…</span>}
              </div>
              <div className="text-sm font-medium">
                ₱{Number(distanceAddon.addonPhp).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
          {(isCOD || manualDelivery) && distanceErr && (
            <div className="text-xs text-rose-700">{distanceErr}</div>
          )}

                     {/* Free Delivery Notice */}
           {(isCOD || manualDelivery) && deliveryFee === 0 && (
             <div className="flex items-center justify-between">
               <div className="text-sm text-green-600">Delivery Fee</div>
               <div className="text-sm font-medium text-green-600">FREE</div>
             </div>
           )}

           {/* Weight-Based Fee */}
           {(isCOD || manualDelivery) && weightBasedFee > 0 && (
             <div className="flex items-center justify-between">
               <div className="text-sm text-ink/70">
                 Weight Fee ({orderWeight.toFixed(2)}kg)
               </div>
               <div className="text-sm font-medium">
                 ₱{weightBasedFee.toLocaleString(undefined, {
                   minimumFractionDigits: 2,
                   maximumFractionDigits: 2,
                 })}
               </div>
             </div>
           )}

           {/* Rider Compensation Info */}
           {(isCOD || manualDelivery) && riderCompensation && (
             <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
               <div className="text-sm font-medium text-blue-800">Rider Compensation</div>
               <div className="space-y-1 text-xs text-blue-700">
                 <div className="flex justify-between">
                   <span>Base Rate:</span>
                   <span>₱{riderCompensation.flatRate}</span>
                 </div>
                 {Number(riderCompensation.distanceAddonForRider || 0) > 0 && (
                   <div className="flex justify-between">
                     <span>Distance Add-on (100%):</span>
                     <span>₱{Number(riderCompensation.distanceAddonForRider).toFixed(2)}</span>
                   </div>
                 )}
                 {riderCompensation.weightFeeForRider > 0 && (
                   <div className="flex justify-between">
                     <span>Weight Fee (90%):</span>
                     <span>₱{riderCompensation.weightFeeForRider.toFixed(2)}</span>
                   </div>
                 )}
                 <div className="flex justify-between font-medium border-t border-blue-300 pt-1">
                   <span>Total Rider Take:</span>
                   <span>₱{riderCompensation.total.toFixed(2)}</span>
                 </div>
               </div>
             </div>
           )}

          {/* Discount Summary */}
          {appliedDiscounts.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-green-600">Total Discounts</div>
              <div className="text-sm font-medium text-green-600">
                -₱{(
                  appliedDiscounts.reduce((total, discount) => {
                    if (discount.discountType === 'percentage') {
                      return total + (subtotal * discount.discountAmount) / 100;
                    } else if (discount.discountType === 'fixed') {
                      return total + discount.discountAmount;
                    } else if (discount.discountType === 'free_shipping') {
                      return total + deliveryFee;
                    }
                    return total;
                  }, 0)
                ).toFixed(2)}
              </div>
            </div>
          )}

          {/* Grand Total */}
          <div className="flex items-center justify-between border-t pt-3">
            <div className="text-xl font-semibold">Total</div>
            <div className="text-xl font-bold">
              ₱
              {Number(grandTotal).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>

          <div className="hidden sm:block">
            <button className="btn btn-primary w-full py-3 text-base font-medium" disabled={!canSubmit} type="submit">
              {busy ? "Processing…" : "Place Order"}
            </button>
          </div>
        </form>
        {/* Sticky mobile submit bar */}
        <div className="sm:hidden fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur shadow-lg">
          <div className="p-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <div className="text-gray-600 leading-tight">Total</div>
              <div className="font-mono font-bold text-lg">₱{grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <button 
              className="btn btn-primary py-3 px-6 text-base font-medium min-w-[120px]" 
              form="checkout-form" 
              type="submit" 
              disabled={!canSubmit}
            >
              {busy ? "Processing…" : "Place Order"}
            </button>
          </div>
        </div>
              </div>
      </div>
      
      {/* SignUpModal */}
      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        onSignUpSuccess={(user) => {
          // User successfully signed up, now they can continue with their order
          setShowSignUpModal(false);
          // The form will automatically work now since user is authenticated
          notify('Account created successfully! You can now complete your order.', 'success');
        }}
      />
      
      </PageBackground>
    </>
  );
}
